import { useEffect, useMemo, useState } from 'react';
import {
  deliveryOrdersAPI,
  getApiErrorMessage,
  paymentAPI,
  type DeliveryOrderRowDto,
} from '@matix/api-client';
import type { PaymentType } from '@matix/types';
import { roundMoney } from '@matix/utils';
import { toast } from '../../store/toast';
import { useLang } from '../../lib/language';
import { moneyPrecision, moneyEpsilon, amountStep } from '../../store/currency';
import { useCurrency } from '../print/useCurrency';

/**
 * Take the money for a delivery order at hand-over.
 *
 * A near-copy of the call centre's `SettleDialog` — same shape, same split
 * behaviour, same wording — because it is the same act at the same counter,
 * and a cashier should not have to learn two dialogs for it. What it does NOT
 * carry is gift cards: redeeming one needs the order path's
 * `applyOrderGiftCardsInTx` inside the settlement transaction, and until that
 * is wired here a gift card at the door is taken at the till, not on this
 * screen. Offering a button that books tender against no card would drain a
 * balance the receipt never credited.
 *
 * Deliberately NOT the till's PaymentModal: that settles the CART, and this
 * order is already written.
 */
export function DeliverySettleDialog({
  order,
  onClose,
  onSettled,
}: {
  order: DeliveryOrderRowDto | null;
  onClose: () => void;
  onSettled: (order: DeliveryOrderRowDto) => void;
}): JSX.Element | null {
  const { t, lang } = useLang();
  const currency = useCurrency();
  const precision = moneyPrecision();
  const money = (n: number) =>
    `${(Number.isFinite(n) ? n : 0).toFixed(Math.max(0, Math.min(8, precision)))} ${currency.code}`;
  const round = (n: number) => roundMoney(n, precision);

  const [types, setTypes] = useState<PaymentType[]>([]);
  // `amount` is kept as the RAW STRING the cashier is typing, not a number.
  // Round-tripping through `Number()` on every keystroke makes a decimal point
  // untypeable: "0." parses to 0, re-renders as "0", and eats the point.
  const [splits, setSplits] = useState<Array<{ payment_type_id: number; amount: string }>>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setSplits([]);
    setBusy(false);
  }, [order?.id]);

  useEffect(() => {
    let cancelled = false;
    paymentAPI
      .listTypes()
      .then((res) => {
        if (cancelled) return;
        // The Multi sentinel (0) is a HEADER stand-in the server writes itself
        // on a split, and Unpaid (-1) is the state we are leaving. Neither is
        // something a customer can hand over.
        setTypes(res.payment_types.filter((p) => p.code !== 0 && p.code !== -1));
      })
      .catch((err) => toast(getApiErrorMessage(err), 'error'));
    return () => {
      cancelled = true;
    };
  }, []);

  // The amounts actually SENT — rounded once, here, so the balance check below
  // and the request body agree by construction. Rounding the check at tenant
  // precision but sending an unrounded number is how a 0-decimal currency
  // produced a balanced dialog and a 422 from the server.
  const payload = useMemo(
    () =>
      splits.map((s) => ({
        payment_type_id: s.payment_type_id,
        amount: round(Number(s.amount) || 0),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [splits, precision],
  );
  const paid = payload.reduce((sum, s) => sum + s.amount, 0);
  const remaining = order ? round(order.net_value - paid) : 0;

  if (!order) return null;

  function addSplit(type: PaymentType): void {
    // Default to the whole remaining balance — one tender for the whole thing
    // is the overwhelmingly common case, and making it a single tap is the
    // difference between this dialog being used and being worked around.
    const amount = round(Math.max(0, remaining));
    if (amount <= 0 && splits.length > 0) return;
    setSplits((prev) => [...prev, { payment_type_id: type.id, amount: String(amount) }]);
  }

  // `moneyEpsilon()`, never a hardcoded cent: on a 3-decimal tenant a 0.008 KWD
  // shortfall reads as "fully paid" against 0.01. The store documents exactly
  // this failure. Every amount must also be positive — the server's schema
  // requires it, and a zero-total order would otherwise post an invalid body.
  const balanced =
    splits.length > 0 &&
    payload.every((p) => p.amount > 0) &&
    Math.abs(remaining) <= moneyEpsilon();

  async function handleConfirm(): Promise<void> {
    if (!order || !balanced) return;
    setBusy(true);
    try {
      const res = await deliveryOrdersAPI.settle(order.id, { payments: payload });
      toast(
        t({ en: `Payment recorded — ${res.payment_label}`, ar: `تم تسجيل الدفع — ${res.payment_label}` }),
        'success',
      );
      onSettled(res.order);
      onClose();
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
      setBusy(false);
    }
  }

  const who = order.customer_name || order.customer_name_ar || t({ en: 'Walk-in', ar: 'عميل نقدي' });

  return (
    <div className="cc-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal cc-settle">
        <header className="cc-drawer-head">
          <div>
            <div className="cc-drawer-title">{t({ en: 'Take payment', ar: 'تحصيل الدفع' })}</div>
            <div className="cc-drawer-sub">
              #{order.entry_no ?? order.id} · {who}
              {order.driver_name ? ` · ${order.driver_name}` : ''}
            </div>
          </div>
          <button type="button" className="cc-drawer-close" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="cc-settle-total">
          <span>{t({ en: 'Total due', ar: 'المستحق' })}</span>
          <strong>{money(order.net_value)}</strong>
        </div>

        <div className="cc-settle-methods">
          {types.map((p) => (
            <button
              key={p.id}
              type="button"
              className="cc-settle-method"
              onClick={() => addSplit(p)}
            >
              {lang === 'ar' ? p.name_ar || p.name : p.name}
            </button>
          ))}
        </div>

        {splits.length > 0 && (
          <ul className="cc-settle-splits">
            {splits.map((s, i) => {
              const type = types.find((p) => p.id === s.payment_type_id);
              return (
                <li key={`${s.payment_type_id}-${i}`}>
                  <span>
                    {type ? (lang === 'ar' ? type.name_ar || type.name : type.name) : '—'}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step={amountStep()}
                    value={s.amount}
                    onChange={(e) =>
                      setSplits((prev) =>
                        prev.map((x, ix) => (ix === i ? { ...x, amount: e.target.value } : x)),
                      )
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setSplits((prev) => prev.filter((_, ix) => ix !== i))}
                    title={t({ en: 'Remove', ar: 'حذف' })}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className={`cc-settle-balance ${balanced ? 'cc-settle-balance--ok' : ''}`}>
          <span>{t({ en: 'Remaining', ar: 'المتبقي' })}</span>
          <strong>{money(remaining)}</strong>
        </div>

        <button
          type="button"
          className="cc-send"
          disabled={!balanced || busy}
          onClick={() => void handleConfirm()}
        >
          {busy ? t({ en: 'Posting…', ar: 'جارٍ الترحيل…' }) : t({ en: 'Confirm payment', ar: 'تأكيد الدفع' })}
        </button>

        <p className="cc-modal-foot">
          {t({
            en: 'This records the tender on the existing receipt and puts the cash in your open shift. No second receipt is created.',
            ar: 'يسجل هذا طريقة الدفع على الفاتورة الحالية ويضع النقدية في ورديتك المفتوحة. لا يتم إنشاء فاتورة ثانية.',
          })}
        </p>
      </div>
    </div>
  );
}
